import {
  aggregateByDay,
  aggregateByMonth,
  aggregateDebtByDay,
  aggregateDebtByMonth,
  assertViewRangeMonths,
  cashbookViewSchema,
  computeBalanceAtStart,
  computeCashbookBalance,
  computeCashbookPeriodTotals,
  computeDebtBalanceAtStart,
  computeDebtPeriodTotals,
  computeDebtRunningBalance,
  computeNetPosition,
  computeRunningBalance,
  computeTotalDebts,
  debtSheetFooter,
  hasStaleCashbookOpeningSettings,
  isCashbookOpeningConfigured,
  localTodayIso,
  openingDebtAmountOrZero,
  sheetFooter,
  todayIso,
  type CashbookDebtSheetFooter,
  type CashbookDebtSheetRow,
  type CashbookEntryRecord,
  type CashbookOpeningDebtLine,
  type CashbookCategory,
  type CashbookDebtCategory,
  type CashbookSheetFooter,
  type CashbookSheetRow,
  type CashbookViewMode
} from '@finvox/shared/cashbook';
import { roundMoney, toMoney } from '@finvox/shared/money';
import { getFirestoreDb } from '@/lib/firebase';
import { loadCashbookContext, syncVersionString, type CashbookContext } from './context';

export type CashbookSummaryResult = {
  treasury: string;
  openingAmount: string;
  openingDebtAmount: string;
  openingDebtLines: CashbookOpeningDebtLine[];
  openingDate?: string;
  periodIn: string;
  periodOut: string;
  periodNet: string;
  totalDebts: string;
  netPosition: string;
  periodDebtIncurred: string;
  periodDebtPaid: string;
  periodDebtNet: string;
  todayTreasury: string;
  todayTotalDebts: string;
  todayNetPosition: string;
  isOpeningSet: boolean;
  canEditOpening: boolean;
  hasStaleOpeningSettings: boolean;
  syncVersion?: string;
  categories?: CashbookCategory[];
  debtCategories?: CashbookDebtCategory[];
};

export type CashbookViewResult = CashbookSummaryResult & {
  rows: CashbookSheetRow[];
  footer: CashbookSheetFooter;
  debtRows: CashbookDebtSheetRow[];
  debtFooter: CashbookDebtSheetFooter;
  entries: CashbookEntryRecord[];
};

export function buildSummaryFromContext(
  ctx: CashbookContext,
  startDate: string,
  endDate: string,
  today: string = todayIso()
): CashbookSummaryResult {
  const { settings, movements, hasActiveEntries } = ctx;
  const isOpeningSet = isCashbookOpeningConfigured(settings);
  const hasStaleOpeningSettings = hasStaleCashbookOpeningSettings(settings);

  const emptyDebt = {
    openingDebtAmount: '0.00',
    openingDebtLines: [] as CashbookOpeningDebtLine[],
    totalDebts: '0.00',
    netPosition: '0.00',
    periodDebtIncurred: '0.00',
    periodDebtPaid: '0.00',
    periodDebtNet: '0.00',
    todayTreasury: '0.00',
    todayTotalDebts: '0.00',
    todayNetPosition: '0.00'
  };

  if (!isOpeningSet) {
    return {
      treasury: '0.00',
      openingAmount: '0.00',
      periodIn: '0.00',
      periodOut: '0.00',
      periodNet: '0.00',
      ...emptyDebt,
      isOpeningSet: false,
      canEditOpening: !hasActiveEntries,
      hasStaleOpeningSettings,
      syncVersion: syncVersionString(settings),
      categories: settings.categories ?? [],
      debtCategories: settings.debtCategories ?? []
    };
  }

  const { periodIn, periodOut, periodNet } = computeCashbookPeriodTotals(movements, {
    startDate,
    endDate
  });
  const { periodDebtIncurred, periodDebtPaid, periodDebtNet } = computeDebtPeriodTotals(
    ctx.debtMovements,
    { startDate, endDate }
  );
  const treasury = computeCashbookBalance(settings, ctx.entries, endDate);
  const totalDebts = computeTotalDebts(settings, ctx.debtEntries, endDate);
  const todayTreasury = computeCashbookBalance(settings, ctx.entries, today);
  const todayTotalDebts = computeTotalDebts(settings, ctx.debtEntries, today);

  return {
    treasury,
    openingAmount: settings.openingAmount ?? '0.00',
    openingDebtAmount: openingDebtAmountOrZero(settings),
    openingDebtLines: settings.openingDebtLines ?? [],
    openingDate: settings.openingDate,
    periodIn,
    periodOut,
    periodNet,
    totalDebts,
    netPosition: computeNetPosition(treasury, totalDebts),
    periodDebtIncurred,
    periodDebtPaid,
    periodDebtNet,
    todayTreasury,
    todayTotalDebts,
    todayNetPosition: computeNetPosition(todayTreasury, todayTotalDebts),
    isOpeningSet,
    canEditOpening: !hasActiveEntries,
    hasStaleOpeningSettings: false,
    syncVersion: syncVersionString(settings),
    categories: settings.categories ?? [],
    debtCategories: settings.debtCategories ?? []
  };
}

export function buildSheetFromContext(
  ctx: CashbookContext,
  startDate: string,
  endDate: string,
  viewMode: CashbookViewMode
): { rows: CashbookSheetRow[]; footer: CashbookSheetFooter; treasury: string } {
  const { settings, entries, movements } = ctx;
  const emptyFooter = { totalIn: '0.00', totalOut: '0.00', net: '0.00', endBalance: '0.00' };

  if (!isCashbookOpeningConfigured(settings)) {
    return { rows: [], footer: emptyFooter, treasury: '0.00' };
  }

  // Period totals may use the full selected range; running balances must start at/after opening.
  const ledgerStart =
    settings.openingDate && startDate < settings.openingDate ? settings.openingDate : startDate;
  const openingAtStart = computeBalanceAtStart(settings, entries, ledgerStart);
  // openingAtStart already includes activity before ledgerStart — only apply from there.
  const rangeMovements = movements.filter((m) => m.date >= ledgerStart);
  const detail = computeRunningBalance(openingAtStart, rangeMovements);
  const treasury = computeCashbookBalance(settings, entries, endDate);

  let rows: CashbookSheetRow[];
  if (viewMode === 'day') {
    const dayRows = detail.filter((r) => r.date >= startDate && r.date <= endDate);
    if (startDate === settings.openingDate) {
      const openingRow: CashbookSheetRow = {
        id: 'opening-row',
        date: settings.openingDate!,
        label: 'Starting balance',
        inAmount: '0.00',
        outAmount: '0.00',
        balance: roundMoney(settings.openingAmount!),
        source: 'opening'
      };
      rows = [openingRow, ...dayRows];
    } else if (
      dayRows.length === 0 &&
      settings.openingDate &&
      startDate > settings.openingDate
    ) {
      rows = [
        {
          id: `carry-${startDate}`,
          date: startDate,
          label: startDate,
          inAmount: '0.00',
          outAmount: '0.00',
          balance: treasury,
          source: 'cashbook'
        }
      ];
    } else {
      rows = dayRows;
    }
  } else if (viewMode === 'month') {
    rows = aggregateByDay(detail, ledgerStart, endDate, openingAtStart);
  } else if (viewMode === 'year' || viewMode === 'multi') {
    rows = aggregateByMonth(
      detail,
      ledgerStart.slice(0, 7),
      endDate.slice(0, 7),
      openingAtStart
    );
  } else {
    rows = aggregateByDay(detail, ledgerStart, endDate, openingAtStart);
  }

  const footer = sheetFooter(rows, treasury);
  return { rows, footer, treasury };
}

export function buildDebtSheetFromContext(
  ctx: CashbookContext,
  startDate: string,
  endDate: string,
  viewMode: CashbookViewMode
): { debtRows: CashbookDebtSheetRow[]; debtFooter: CashbookDebtSheetFooter; totalDebts: string } {
  const { settings, debtEntries, debtMovements } = ctx;
  const emptyFooter = {
    totalIncurred: '0.00',
    totalPaid: '0.00',
    net: '0.00',
    endBalance: '0.00'
  };

  if (!isCashbookOpeningConfigured(settings)) {
    return { debtRows: [], debtFooter: emptyFooter, totalDebts: '0.00' };
  }

  const ledgerStart =
    settings.openingDate && startDate < settings.openingDate ? settings.openingDate : startDate;
  const openingDebtAtStart = computeDebtBalanceAtStart(settings, debtEntries, ledgerStart);
  // openingDebtAtStart already includes activity before ledgerStart — only apply from there.
  const debtFromStart = debtMovements.filter((m) => m.date >= ledgerStart);
  const detail = computeDebtRunningBalance(openingDebtAtStart, debtFromStart);
  const totalDebts = computeTotalDebts(settings, debtEntries, endDate);
  const openingDebt = openingDebtAmountOrZero(settings);

  let debtRows: CashbookDebtSheetRow[];
  if (viewMode === 'day') {
    const dayRows = detail.filter((r) => r.date >= startDate && r.date <= endDate);
    if (startDate === settings.openingDate && toMoney(openingDebt).gt(0)) {
      const openingRow: CashbookDebtSheetRow = {
        id: 'opening-debt-row',
        date: settings.openingDate!,
        label: 'Opening debts',
        incurredAmount: '0.00',
        paidAmount: '0.00',
        balance: openingDebt,
        source: 'opening_debt'
      };
      debtRows = [openingRow, ...dayRows];
    } else if (
      dayRows.length === 0 &&
      settings.openingDate &&
      startDate > settings.openingDate &&
      toMoney(totalDebts).gt(0)
    ) {
      debtRows = [
        {
          id: `carry-debt-${startDate}`,
          date: startDate,
          label: startDate,
          incurredAmount: '0.00',
          paidAmount: '0.00',
          balance: totalDebts,
          source: 'debt'
        }
      ];
    } else {
      debtRows = dayRows;
    }
  } else if (viewMode === 'month') {
    debtRows = aggregateDebtByDay(detail, ledgerStart, endDate, openingDebtAtStart);
  } else if (viewMode === 'year' || viewMode === 'multi') {
    debtRows = aggregateDebtByMonth(
      detail,
      ledgerStart.slice(0, 7),
      endDate.slice(0, 7),
      openingDebtAtStart
    );
  } else {
    debtRows = aggregateDebtByDay(detail, ledgerStart, endDate, openingDebtAtStart);
  }

  const debtFooter = debtSheetFooter(debtRows, totalDebts);
  return { debtRows, debtFooter, totalDebts };
}

export async function getCashbookView(orgId: string, input: unknown): Promise<CashbookViewResult> {
  const { startDate, endDate, viewMode } = cashbookViewSchema.parse(input);
  assertViewRangeMonths(startDate, endDate);
  const db = getFirestoreDb();
  const ctx = await loadCashbookContext(db, orgId, endDate);
  const summary = buildSummaryFromContext(ctx, startDate, endDate, localTodayIso());
  const sheet = buildSheetFromContext(ctx, startDate, endDate, viewMode);
  const debtSheet = buildDebtSheetFromContext(ctx, startDate, endDate, viewMode);
  return {
    ...summary,
    rows: sheet.rows,
    footer: sheet.footer,
    debtRows: debtSheet.debtRows,
    debtFooter: debtSheet.debtFooter,
    entries: ctx.entries
  };
}
